import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCoinIconUrl } from '../../utils/helpers';
import './UnusualActivityModal.css';

function UnusualActivityModal({ isOpen, onClose, unusualActivity, changeTimeframe }) {
  const { t } = useTranslation();
  const [activeTimeframe, setActiveTimeframe] = useState(unusualActivity.timeframe || '1h');

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const { coins, loading } = unusualActivity;

  // Handle timeframe change
  const handleTimeframeChange = (timeframe) => {
    setActiveTimeframe(timeframe);
    changeTimeframe(timeframe);
  };

  // Format volume
  const formatVolume = (volume) => {
    if (volume >= 1_000_000_000) {
      return `$${(volume / 1_000_000_000).toFixed(1)}B`;
    }
    if (volume >= 1_000_000) {
      return `$${(volume / 1_000_000).toFixed(1)}M`;
    }
    if (volume >= 1_000) {
      return `$${(volume / 1_000).toFixed(0)}K`;
    }
    return `$${volume.toFixed(0)}`;
  };

  // Get heat level class
  const getHeatClass = (heatLevel) => {
    return `heat-${Math.min(heatLevel, 5)}`;
  };

  return (
    <div className="ua-modal-overlay" onClick={onClose}>
      <div className="ua-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="ua-modal-header">
          <h5 className="ua-modal-title">
            <span>&#129517;</span> {t('unusualActivity.heatmap')}
          </h5>
          <button className="ua-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="ua-modal-body">
          <p className="heatmap-description">
            {t('unusualActivity.heatmapDesc')}
          </p>

          <div className="heatmap-timeframe">
            <button
              className={`timeframe-btn ${activeTimeframe === '1h' ? 'active' : ''}`}
              onClick={() => handleTimeframeChange('1h')}
            >
              {t('unusualActivity.1hour')}
            </button>
            <button
              className={`timeframe-btn ${activeTimeframe === '4h' ? 'active' : ''}`}
              onClick={() => handleTimeframeChange('4h')}
            >
              {t('unusualActivity.4hours')}
            </button>
            <button
              className={`timeframe-btn ${activeTimeframe === '12h' ? 'active' : ''}`}
              onClick={() => handleTimeframeChange('12h')}
            >
              {t('unusualActivity.12hours')}
            </button>
          </div>

          {loading ? (
            <div className="ua-modal-loading">{t('unusualActivity.loading')}</div>
          ) : coins.length === 0 ? (
            <div className="ua-modal-empty">{t('unusualActivity.noActivity')}</div>
          ) : (
            <div className="crypto-heatmap">
              {coins.map((coin) => (
                <div
                  key={coin.symbol}
                  className={`heatmap-item ${getHeatClass(coin.heatLevel)}`}
                  data-coin={coin.symbol}
                  data-activity={coin.activityChange}
                >
                  <div className="coin-header">
                    <img
                      src={getCoinIconUrl(coin.symbol)}
                      alt={coin.symbol}
                      className="coin-logo"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span className="coin-symbol">{coin.symbol}</span>
                  </div>
                  <span className="activity-percent">+{coin.activityChange}%</span>
                  <div className="coin-details">
                    <div className="ls-ratio">
                      <div className="ls-bar-container">
                        <div
                          className="ls-bar long"
                          style={{ width: `${coin.longPercent}%` }}
                          data-long={coin.longPercent}
                        ></div>
                        <div
                          className="ls-bar short"
                          style={{ width: `${coin.shortPercent}%` }}
                          data-short={coin.shortPercent}
                        ></div>
                      </div>
                      <div className="ls-percentages">
                        <span className="ls-value long">{coin.longPercent}%</span>
                        <span className="ls-value short">{coin.shortPercent}%</span>
                      </div>
                    </div>
                    <div className="volume-info">
                      <span className="volume-label">{t('unusualActivity.vol')}:</span>
                      <span className="volume-value">{formatVolume(coin.volume)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="heatmap-legend">
            <span className="legend-label">{t('unusualActivity.activityLevel')}:</span>
            <div className="legend-items">
              <div className="legend-item">
                <span className="legend-color low"></span> {t('unusualActivity.lowRange')}
              </div>
              <div className="legend-item">
                <span className="legend-color medium"></span> {t('unusualActivity.mediumRange')}
              </div>
              <div className="legend-item">
                <span className="legend-color high"></span> {t('unusualActivity.highRange')}
              </div>
              <div className="legend-item">
                <span className="legend-color extreme"></span> {t('unusualActivity.extremeRange')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UnusualActivityModal;
